import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as LucideIcons from 'lucide-react'
import {
  X,
  ChevronRight,
  Filter,
  Sparkles,
  Eye,
  FileText,
  Mail,
  BarChart3,
  ExternalLink
} from 'lucide-react'
import { GlassCard } from '../ui/glass/GlassCard'
import { GlassBadge } from '../ui/glass/GlassBadge'
import {
  TrustTier,
  TRUST_TIER_CONFIG,
  DEFAULT_AGENTS,
  AgentRingAgent
} from '@/types/agentRing'

// Example outputs for each agent
const AGENT_EXAMPLE_OUTPUTS: Record<string, { type: string; title: string; preview: string }[]> = {
  'analytics': [
    { type: 'report', title: 'Weekly Performance Report', preview: 'Traffic up 23%, conversions improved by 15%...' },
    { type: 'insight', title: 'Attribution Analysis', preview: 'Email drives 40% of conversions, Social 25%...' },
  ],
  'technical-seo': [
    { type: 'audit', title: 'Core Web Vitals Report', preview: 'LCP: 2.1s (Good), FID: 45ms (Good), CLS: 0.08...' },
    { type: 'fix', title: 'Schema Markup Added', preview: 'Organization, Product, and FAQ schema deployed...' },
  ],
  'local-seo': [
    { type: 'update', title: 'GBP Optimization', preview: 'Updated business hours, added 12 new photos...' },
    { type: 'report', title: 'Local Rankings', preview: 'Ranking #3 for "marketing agency near me"...' },
  ],
  'seo-keyword': [
    { type: 'research', title: 'Keyword Opportunities', preview: '47 new keywords identified with low competition...' },
    { type: 'strategy', title: 'Content Gap Analysis', preview: 'Competitors ranking for 23 terms we\'re missing...' },
  ],
  'content-strategy': [
    { type: 'plan', title: 'Q1 Content Calendar', preview: '24 blog posts, 8 whitepapers, 12 case studies...' },
    { type: 'brief', title: 'Blog Post Brief', preview: '"10 AI Marketing Trends for 2025" - 2,500 words...' },
  ],
  'email-automation': [
    { type: 'campaign', title: 'Welcome Sequence', preview: '7-email series, 42% open rate, 12% CTR...' },
    { type: 'segment', title: 'Audience Segments', preview: '5 segments created based on engagement...' },
  ],
  'social-scheduler': [
    { type: 'calendar', title: 'This Week\'s Posts', preview: '21 posts scheduled across 4 platforms...' },
    { type: 'post', title: 'LinkedIn Post', preview: '"Why AI agents are the future of marketing..."' },
  ],
  'video-content': [
    { type: 'script', title: 'Product Demo Script', preview: '3-minute video, 5 scenes, 2 CTAs...' },
    { type: 'metadata', title: 'YouTube Optimization', preview: 'Title, description, tags, and thumbnails ready...' },
  ],
  'backlink-builder': [
    { type: 'prospects', title: 'Link Opportunities', preview: '34 high-DA sites identified for outreach...' },
    { type: 'template', title: 'Outreach Email', preview: 'Personalized pitch for guest posting...' },
  ],
  'pr-outreach': [
    { type: 'release', title: 'Press Release Draft', preview: '"Synap Launches Revolutionary AI Marketing OS..."' },
    { type: 'pitch', title: 'Journalist Pitch', preview: 'Personalized pitch for TechCrunch reporter...' },
  ],
  'ad-optimizer': [
    { type: 'creative', title: 'Ad Variations', preview: '6 headline variants, 4 image options tested...' },
    { type: 'recommendation', title: 'Budget Reallocation', preview: 'Shift 20% from Display to Search for +15% ROAS...' },
  ],
  'ppc-agent': [
    { type: 'campaign', title: 'Search Campaign', preview: '150 keywords, 12 ad groups, $2.50 avg CPC...' },
    { type: 'optimization', title: 'Bid Adjustments', preview: 'Mobile +15%, Desktop -5%, Tablet -10%...' },
  ],
  'influencer-connect': [
    { type: 'discovery', title: 'Influencer Matches', preview: '18 micro-influencers in your niche identified...' },
    { type: 'outreach', title: 'Partnership Proposal', preview: 'Collaboration terms: 3 posts, 2 stories...' },
  ],
  'podcast-agent': [
    { type: 'research', title: 'Podcast Opportunities', preview: '12 relevant podcasts accepting guests...' },
    { type: 'pitch', title: 'Guest Pitch', preview: 'Topic: "AI Revolution in Marketing"...' },
  ],
  'conversion-agent': [
    { type: 'analysis', title: 'Funnel Analysis', preview: 'Drop-off at checkout: 34%, recommended fixes...' },
    { type: 'test', title: 'A/B Test Results', preview: 'Variant B: +18% conversion, 95% confidence...' },
  ],
  'mobile-marketing': [
    { type: 'alert', title: 'Compliance Alert', preview: 'SMS opt-in rate below threshold, action needed...' },
    { type: 'trend', title: 'Mobile Trends', preview: 'App usage up 23%, push engagement stable...' },
  ],
}

interface AgentShowcaseProps {
  onTryDashboard?: () => void
  className?: string
}

export function AgentShowcase({ onTryDashboard, className = '' }: AgentShowcaseProps) {
  const [selectedAgent, setSelectedAgent] = useState<AgentRingAgent | null>(null)
  const [filterTier, setFilterTier] = useState<TrustTier | 'all'>('all')
  const [hoveredAgent, setHoveredAgent] = useState<string | null>(null)

  // Filter agents by trust tier
  const filteredAgents = useMemo(() => {
    if (filterTier === 'all') return DEFAULT_AGENTS
    return DEFAULT_AGENTS.filter(agent => agent.trustTier === filterTier)
  }, [filterTier])

  // Get Lucide icon component by name
  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as unknown as Record<string, React.ElementType>)[iconName]
    return Icon || LucideIcons.Bot
  }

  // Get output type icon
  const getOutputIcon = (type: string) => {
    switch (type) {
      case 'report':
      case 'audit':
        return BarChart3
      case 'insight':
      case 'analysis':
        return Eye
      case 'campaign':
      case 'post':
      case 'calendar':
        return FileText
      case 'email':
      case 'outreach':
      case 'pitch':
        return Mail
      default:
        return Sparkles
    }
  }

  return (
    <div className={`relative ${className}`}>
      {/* Header with Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Meet Your 16 AI Marketing Agents
          </h2>
          <p className="text-gray-400">
            Click any agent to see what they can do for you
          </p>
        </div>

        {/* Trust Tier Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-white/50" />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterTier('all')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                filterTier === 'all'
                  ? 'bg-white text-gray-900'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
              }`}
            >
              All
            </button>
            {([1, 2, 3, 4, 5] as TrustTier[]).map(tier => {
              const config = TRUST_TIER_CONFIG[tier]
              return (
                <button
                  key={tier}
                  onClick={() => setFilterTier(tier)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                    filterTier === tier
                      ? 'text-white'
                      : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
                  }`}
                  style={filterTier === tier ? { backgroundColor: config.color } : {}}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: config.color }}
                  />
                  {config.name}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Agent Grid */}
      <motion.div
        layout
        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3"
      >
        <AnimatePresence mode="popLayout">
          {filteredAgents.map((agent, index) => {
            const Icon = getIcon(agent.icon)
            const tierConfig = TRUST_TIER_CONFIG[agent.trustTier]
            const isHovered = hoveredAgent === agent.id

            return (
              <motion.button
                key={agent.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
                onClick={() => setSelectedAgent(agent)}
                onMouseEnter={() => setHoveredAgent(agent.id)}
                onMouseLeave={() => setHoveredAgent(null)}
                className={`
                  relative p-4 rounded-xl border transition-all duration-200
                  flex flex-col items-center text-center
                  ${isHovered
                    ? 'bg-gray-800/80 border-gray-600 scale-105 shadow-lg'
                    : 'bg-gray-900/50 border-gray-700/50 hover:border-gray-600'
                  }
                `}
                style={{
                  boxShadow: isHovered ? `0 0 20px ${tierConfig.color}30` : 'none'
                }}
              >
                {/* Trust Tier Indicator */}
                <div
                  className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ backgroundColor: tierConfig.color }}
                  title={`Tier ${agent.trustTier}: ${tierConfig.name}`}
                >
                  {agent.trustTier}
                </div>

                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 transition-colors"
                  style={{
                    backgroundColor: isHovered ? tierConfig.bgColor : 'rgba(55, 65, 81, 0.5)',
                    borderWidth: '1px',
                    borderColor: isHovered ? tierConfig.borderColor : 'transparent'
                  }}
                >
                  <Icon
                    className="h-6 w-6 transition-colors"
                    style={{ color: isHovered ? tierConfig.color : '#9CA3AF' }}
                  />
                </div>

                {/* Name */}
                <span className="text-xs font-medium text-white leading-tight">
                  {agent.shortName}
                </span>

                {/* Category */}
                <span className="text-[10px] text-white/50 mt-0.5">
                  {agent.category}
                </span>
              </motion.button>
            )
          })}
        </AnimatePresence>
      </motion.div>

      {/* Empty State */}
      {filteredAgents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-white/50">No agents at this trust tier</p>
        </div>
      )}

      {/* Agent Detail Modal */}
      <AnimatePresence>
        {selectedAgent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 "
            onClick={() => setSelectedAgent(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg"
            >
              <GlassCard className="overflow-hidden">
                {/* Header */}
                <div
                  className="p-6 border-b border-gray-700/50"
                  style={{ backgroundColor: TRUST_TIER_CONFIG[selectedAgent.trustTier].bgColor }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          borderWidth: '1px',
                          borderColor: TRUST_TIER_CONFIG[selectedAgent.trustTier].borderColor
                        }}
                      >
                        {React.createElement(getIcon(selectedAgent.icon), {
                          className: 'h-7 w-7',
                          style: { color: TRUST_TIER_CONFIG[selectedAgent.trustTier].color }
                        })}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {selectedAgent.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <GlassBadge
                            style={{
                              backgroundColor: TRUST_TIER_CONFIG[selectedAgent.trustTier].bgColor,
                              borderColor: TRUST_TIER_CONFIG[selectedAgent.trustTier].borderColor,
                              color: TRUST_TIER_CONFIG[selectedAgent.trustTier].color
                            }}
                          >
                            Tier {selectedAgent.trustTier}: {TRUST_TIER_CONFIG[selectedAgent.trustTier].name}
                          </GlassBadge>
                          <span className="text-xs text-gray-400">{selectedAgent.category}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedAgent(null)}
                      className="p-2 rounded-lg hover:bg-white/[0.04] transition-colors"
                    >
                      <X className="h-5 w-5 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Description */}
                  <p className="text-gray-300 mb-6">
                    {selectedAgent.description}
                  </p>

                  {/* Trust Tier Explanation */}
                  <div className="mb-6 p-4 rounded-lg bg-gray-800/50 border border-gray-700/50">
                    <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: TRUST_TIER_CONFIG[selectedAgent.trustTier].color }}
                      />
                      Trust Level: {TRUST_TIER_CONFIG[selectedAgent.trustTier].name}
                    </h4>
                    <p className="text-sm text-gray-400">
                      {TRUST_TIER_CONFIG[selectedAgent.trustTier].description}
                    </p>
                  </div>

                  {/* Example Outputs */}
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary-400" />
                      Example Outputs
                    </h4>
                    <div className="space-y-3">
                      {(AGENT_EXAMPLE_OUTPUTS[selectedAgent.id] || []).map((output, idx) => {
                        const OutputIcon = getOutputIcon(output.type)
                        return (
                          <div
                            key={idx}
                            className="p-3 rounded-lg bg-gray-800/30 border border-gray-700/30"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <OutputIcon className="h-4 w-4 text-white/50" />
                              <span className="text-sm font-medium text-white">
                                {output.title}
                              </span>
                            </div>
                            <p className="text-xs text-white/50 pl-6">
                              {output.preview}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* CTA */}
                  {onTryDashboard && (
                    <button
                      onClick={() => {
                        setSelectedAgent(null)
                        onTryDashboard()
                      }}
                      className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors"
                    >
                      See {selectedAgent.shortName} in Action
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AgentShowcase
